import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    IconButton,
    DialogContent,
    Button,
    Typography,
    TextField,
    Select,
    MenuItem,
    SelectChangeEvent,
    Autocomplete,
    useTheme
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useCreateUserStore } from 'Application/store/useUserStore';
import { toast } from 'sonner';
import { CreateInternalUserRequest, UserRole } from 'Application/models';
import { useAgencyStore } from 'Application/store';
import { Controller } from 'react-hook-form';
import { ThemeMode } from 'config';

interface AddNewUserProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}
const CreateBritamUser: React.FC<AddNewUserProps> = ({ isOpen, setIsOpen }) => {
    const { createInternalUser, error } = useCreateUserStore();
    const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
    const [userRole, setUserRole] = useState<UserRole>(UserRole.ROLE_AGENT);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        emailAddress: ''
    });
    const [selectedIntermediaries, setSelectedIntermediaries] = useState<string[]>([]);

    const {
        getUnsupervisedAgenciesName,
        isLoading: isUnsupervisedAgenciesLoading,
        unsupervisedAgenciesData
    } = useAgencyStore((state) => ({
        getUnsupervisedAgenciesName: state.getUnsupervisedAgenciesName,
        isLoading: state.isLoading,
        unsupervisedAgenciesData: state.unsupervisedAgenciesData
    }));

    const { palette } = useTheme();
    const modalTitleColor = palette.mode === ThemeMode.DARK ? '#A0AEC0' : '#4A5568';

    useEffect(() => {
        getUnsupervisedAgenciesName();
    }, []);

    const handleChange = (e: SelectChangeEvent<UserRole>) => {
        setUserRole(e.target.value as UserRole);

        // Clear selected intermediaries if role is changed to something other than CSE/BD
        if (e.target.value !== UserRole.ROLE_CSE) {
            setSelectedIntermediaries([]);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsSubmitLoading(true);
        // Base form data
        const userData: CreateInternalUserRequest = {
            ...formData,
            roleName: userRole
        };

        // Add supervisingIntermediariesNames conditionally if role is CSE/BD
        if (userRole === UserRole.ROLE_CSE) {
            userData.supervisingIntermediariesNames = selectedIntermediaries;
        }

        try {
            await createInternalUser(userData);
            toast.success('User created successfully');
            setIsOpen(false);

            // Clear the form data
            setFormData({
                firstName: '',
                lastName: '',
                emailAddress: ''
            });
            setUserRole(UserRole.ROLE_AGENT); // Reset the role selection
            setSelectedIntermediaries([]); // Clear intermediaries
        } catch {
            // No need to capture the error here; it is already stored in `error`
            if (error) {
                toast.error(`Failed to create user: ${error}`);
            } else {
                toast.error('Failed to create user: An unknown error occurred');
            }
        } finally {
            setIsSubmitLoading(false);
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    width: '70%',
                    maxWidth: '600px'
                }
            }}
        >
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: modalTitleColor }}>Create new user</span>
                    <IconButton onClick={handleClose}>
                        <ClearIcon />
                    </IconButton>
                </div>
                <div style={{ marginTop: '0.5rem', color: '#718096', fontSize: '0.875rem' }}>Please fill in the following details</div>
            </DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Full Name
                        </Typography>
                        <TextField
                            placeholder="Enter Full Name"
                            required
                            name="firstName"
                            variant="outlined"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </div>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Other Name
                        </Typography>
                        <TextField
                            placeholder="Enter Other Name"
                            required
                            name="lastName"
                            variant="outlined"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </div>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Email
                        </Typography>
                        <TextField
                            placeholder="Enter Email"
                            required
                            name="emailAddress"
                            variant="outlined"
                            type="email"
                            value={formData.emailAddress}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </div>

                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Select A Role
                        </Typography>
                        <Select value={userRole} onChange={handleChange} fullWidth>
                            <MenuItem value={UserRole.ROLE_ADMINISTRATOR}>Administrator</MenuItem>
                            <MenuItem value={UserRole.ROLE_REGIONAL_ADMIN}>Regional administrator</MenuItem>
                            <MenuItem value={UserRole.ROLE_UNDER_WRITER}>Underwriter</MenuItem>
                            <MenuItem value={UserRole.ROLE_ISC}>ISC</MenuItem>
                            <MenuItem value={UserRole.ROLE_CSE}>CSE/BD</MenuItem>
                        </Select>
                    </div>

                    {userRole === UserRole.ROLE_CSE && (
                        <div style={{ paddingBottom: '1rem' }}>
                            <Typography
                                style={{
                                    marginBottom: '0.25rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    color: '#718096'
                                }}
                            >
                                Select the intermediaries
                            </Typography>
                            <Autocomplete
                                multiple
                                options={unsupervisedAgenciesData.map((agency) => agency.name)}
                                value={selectedIntermediaries}
                                onChange={(_event, value) => setSelectedIntermediaries(value)}
                                renderInput={(params) => <TextField {...params} label="Select the intermediaries" fullWidth />}
                            />
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                color: '#4A5568',
                                borderColor: '#4A5568',
                                backgroundColor: '#ffffff'
                            }}
                            disabled={isSubmitLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="outlined"
                            sx={{
                                backgroundColor: '#673ab7',
                                color: '#ffffff',
                                marginLeft: '1.5rem',
                                '&:hover': {
                                    backgroundColor: '#ffffff',
                                    color: '#673ab7',
                                    borderColor: '#673ab7'
                                },
                                '&.Mui-disabled': {
                                    color: '#ffffff',
                                    backgroundColor: '#98A2B3'
                                }
                            }}
                            disabled={isSubmitLoading}
                        >
                            {isSubmitLoading ? 'Submitting...' : 'Submit'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default CreateBritamUser;
